import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import websocketService from '../../../utils/websocketService';

export const useChats = (projectId, user) => {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  
  // Add typing state management
  const [typingUsers, setTypingUsers] = useState({}); // chatId -> { userId: { userName, userColor, timestamp, profileImage } }

  // Add menu state management
  const [activeMenu, setActiveMenu] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const refreshChats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/projects/${projectId}/chats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChats(response.data.chats || []);
    } catch (error) {
      console.error('Error refreshing chats:', error);
    }
  }, [projectId]);

  // Listen for real-time chat updates (user joins/leaves) - RELIABLE LIKE TYPING
  useEffect(() => {
    const handleWebSocketMessage = async (data) => {
      console.log('🎯 ProjectDashboard received WebSocket message:', data);
      
      if (data.type === 'chat_update') {
        const updateType = data.data?.type;
        
        if (updateType === 'user_joined_chat' || updateType === 'user_left_chat') {
          console.log(`🔄 INSTANT chat list update: ${updateType}`);
          
          // IMMEDIATE refresh - no delays, no conditions
          refreshChats();
          
          // Notify sidebar about project chat updates for red dot updates
          window.dispatchEvent(new CustomEvent('projectChatUpdate', {
            detail: {
              type: updateType,
              chatId: data.chatId,
              projectId: projectId,
              activeUsers: data.data?.activeUsers
            }
          }));
        }
        
        // Handle typing events that come through chat_update
        if (updateType === 'user_typing') {
          const { userId, userName, userColor, isTyping, profileImage } = data.data;
          const chatId = data.chatId;
          
          // Don't show our own typing indicator
          if (userId === user?.id) return;
          
          console.log(`Typing event: ${userName} ${isTyping ? 'started' : 'stopped'} typing in chat ${chatId}`);
          
          setTypingUsers(prev => {
            const chatTypingUsers = { ...prev[chatId] };
            const wasTyping = !!chatTypingUsers[userId];
            
            if (isTyping) {
              if (!wasTyping) {
                chatTypingUsers[userId] = {
                  userName,
                  userColor,
                  timestamp: Date.now(),
                  profileImage
                };
              } else {
                chatTypingUsers[userId].timestamp = Date.now();
              }
            } else {
              delete chatTypingUsers[userId];
            }
            
            const newChatTypingUsers = Object.keys(chatTypingUsers).length > 0 ? chatTypingUsers : undefined;
            const oldChatTypingUsers = prev[chatId];
            
            const oldKeys = oldChatTypingUsers ? Object.keys(oldChatTypingUsers) : [];
            const newKeys = newChatTypingUsers ? Object.keys(newChatTypingUsers) : [];
            
            const typingUsersChanged = !oldChatTypingUsers !== !newChatTypingUsers ||
              oldKeys.length !== newKeys.length ||
              oldKeys.some(id => !newChatTypingUsers || !newChatTypingUsers[id]) ||
              newKeys.some(id => !oldChatTypingUsers || !oldChatTypingUsers[id]);
            
            if (typingUsersChanged) {
              return {
                ...prev,
                [chatId]: newChatTypingUsers
              };
            }
            
            return prev;
          });
        }
      }

      if (data.type === 'project_chats_updated') {
        if (data.projectId === projectId) {
          console.log('🔄 Project chats updated event');
          refreshChats();
        }
      }
    };

    console.log('👂 ProjectDashboard setting up RELIABLE WebSocket listener for project:', projectId);
    websocketService.addListener('projectDashboard', handleWebSocketMessage);

    return () => {
      console.log('🧹 ProjectDashboard cleaning up WebSocket listener');
      websocketService.removeListener('projectDashboard');
    };
  }, [projectId, user?.id, refreshChats]);

  // Cleanup stale typing indicators
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const staleThreshold = 60000; // 1 minute timeout to match ChatInput

      setTypingUsers(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach(chatId => {
          const chatTypingUsers = { ...updated[chatId] };
          
          Object.keys(chatTypingUsers).forEach(userId => {
            if (now - chatTypingUsers[userId].timestamp > staleThreshold) {
              delete chatTypingUsers[userId];
              hasChanges = true;
            }
          });

          if (Object.keys(chatTypingUsers).length === 0) {
            delete updated[chatId];
            hasChanges = true;
          } else {
            updated[chatId] = chatTypingUsers;
          }
        });

        return hasChanges ? updated : prev;
      });
    }, 5000); // Check every 5 seconds (less frequent since we have longer timeout)

    return () => clearInterval(cleanupInterval);
  }, []);

  const createNewChat = async () => {
    if (isCreatingChat) return;

    setIsCreatingChat(true);
    try {
      // Navigate to blank chat page for project - chat will be created when first message is sent
      navigate(`/chat?project=${projectId}`);
      
      // Reset the creating state after navigation
      setTimeout(() => {
        setIsCreatingChat(false);
      }, 500);
    } catch (error) {
      console.error('Error navigating to new project chat:', error);
      setIsCreatingChat(false);
    }
  };

  const handleChatClick = (chatId) => {
    navigate(`/chat/${chatId}?project=${projectId}`);
  };

  const handleMenuClick = (e, chatId) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveMenu(chatId);
    setMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const handleRename = (e, chatId, currentTitle) => {
    e.stopPropagation();
    setEditingChatId(chatId);
    setEditValue(currentTitle);
    setActiveMenu(null);
  };

  const handleRenameSubmit = async (chatId) => {
    if (!editValue.trim()) {
      setEditingChatId(null);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Immediately update local state for better UX
      setChats(prevChats => {
        return prevChats.map(chat => 
          chat._id === chatId ? { ...chat, title: editValue } : chat
        );
      });
      
      // Clear editing state
      setEditingChatId(null);
      
      // Make the API call
      await axios.put(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/title`,
        { title: editValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error renaming chat:', error);
      // Refresh chats to restore correct state
      refreshChats();
      throw new Error('Failed to rename chat');
    }
  };

  const handleDelete = (e, chatId) => {
    e.preventDefault();
    e.stopPropagation();
    setChatToDelete(chatId);
    setDeleteModalOpen(true);
    setActiveMenu(null);
  };

  const confirmDelete = async () => {
    if (!chatToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      const deletingChatId = chatToDelete;
      
      // First close modal and update UI to appear responsive
      setDeleteModalOpen(false);
      
      // Update local state immediately to remove the deleted chat
      setChats(prevChats => {
        return prevChats.filter(chat => chat._id !== deletingChatId);
      });
      
      // Reset other state
      setChatToDelete(null);
      setActiveMenu(null);
      
      // Then perform the actual API call
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/chat/${deletingChatId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Notify sidebar that project chats have been updated
      window.dispatchEvent(new CustomEvent('projectChatUpdate', {
        detail: {
          type: 'chat_deleted',
          projectId: projectId,
          chatId: deletingChatId
        }
      }));
    } catch (error) {
      console.error("Error deleting chat:", error);
      // Refresh chat list to ensure UI is correct
      refreshChats();
      throw new Error("Failed to delete chat");
    }
  };

  return {
    chats,
    setChats,
    isCreatingChat,
    typingUsers,
    activeMenu,
    setActiveMenu,
    deleteModalOpen,
    chatToDelete,
    editingChatId,
    editValue,
    setEditValue,
    setEditingChatId,
    menuPosition,
    setDeleteModalOpen,
    refreshChats,
    createNewChat,
    handleChatClick,
    handleMenuClick,
    handleRename,
    handleRenameSubmit,
    handleDelete,
    confirmDelete
  };
}; 